import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const IDLE_WARNING_MS = 28 * 60 * 1000; // 2 minutes before logout
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authed, object = authed
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const warnedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    // CRITICAL: If returning from OAuth callback, skip the /me check —
    // AuthCallback will exchange the session_id and establish the session.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (mounted) {
          setUser(data);
          setMustChangePassword(Boolean(data?.must_change_password));
        }
      } catch {
        if (mounted) setUser(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("mptg_token");
    setUser(false);
    setMustChangePassword(false);
  }, []);

  // ---- Idle auto-logout (30 min) with 28-min warning toast ----
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    warnedRef.current = false;
    warnTimerRef.current = setTimeout(() => {
      if (!user || user === false || warnedRef.current) return;
      warnedRef.current = true;
      toast.warning("Your session expires in 2 minutes due to inactivity", {
        id: "idle-warning",
        duration: 2 * 60 * 1000,
        action: {
          label: "Stay signed in",
          onClick: () => {
            toast.dismiss("idle-warning");
            resetIdleTimer();
          },
        },
      });
    }, IDLE_WARNING_MS);
    idleTimerRef.current = setTimeout(async () => {
      // Only act when actually authed; ignore if user already logged out
      if (user && user !== false) {
        toast.dismiss("idle-warning");
        toast.error("You were signed out after 30 minutes of inactivity");
        await logout();
      }
    }, IDLE_TIMEOUT_MS);
  }, [user, logout]);

  useEffect(() => {
    if (!user || user === false) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      return undefined;
    }
    const handler = () => resetIdleTimer();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    };
  }, [user, resetIdleTimer]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) {
      localStorage.setItem("mptg_token", data.access_token);
    }
    setUser(data.user);
    setMustChangePassword(Boolean(data.must_change_password));
    return data.user;
  };

  const clearMustChange = () => setMustChangePassword(false);

  return (
    <AuthContext.Provider
      value={{ user, setUser, login, logout, loading, mustChangePassword, clearMustChange }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export const ROLE_LABELS = {
  crm_manager: "Admin (CRM Manager)",
  accounts: "Accounts Team",
  process_manager: "Process Manager",
  branch_manager: "Branch Manager",
  regional_manager: "Regional Manager",
  coo_ceo: "COO / CEO",
};
